import * as React from "react";

// Small deterministic PRNG, so a seed always gives the same numbers on the server and in the browser.
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hash(text: string): number {
  let h = 2166136261;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

const between = (random: () => number, min: number, max: number) => min + random() * (max - min);

/**
 * Per-instance animation timing for the holo pieces, as CSS variables: each border turns at its own
 * slow speed from its own starting angle, and each loading aurora drifts on its own schedule, so a
 * page of them never moves in lockstep. Seeded from `useId`, which is stable between server and client.
 */
export function holoTiming(seed: string): React.CSSProperties {
  const random = mulberry32(hash(seed));
  const duration = between(random, 12, 16);
  return {
    "--holo-duration": `${duration.toFixed(2)}s`,
    // A negative delay starts the loop part-way through, so instances begin at different angles.
    "--holo-delay": `-${(random() * duration).toFixed(2)}s`,
    "--holo-drift-a": `${between(random, 10, 13).toFixed(2)}s`,
    "--holo-drift-b": `${between(random, 12, 15).toFixed(2)}s`,
    "--holo-drift-c": `${between(random, 13, 16).toFixed(2)}s`,
  } as React.CSSProperties;
}

export function useHoloTiming(): React.CSSProperties {
  const id = React.useId();
  return React.useMemo(() => holoTiming(id), [id]);
}
